import type { MenuItem, ModificationGroup } from "@/features/menu/menuSlice";
import { hydrateFromOrder } from "./cartSlice";
import type {
  CartHydrationMode,
  CartModifierGroup,
  BackendCartItem,
} from "./cartSlice";

export interface OrderToCartMenuLookup {
  items: MenuItem[];
  itemsById: Record<string, MenuItem>;
  modificationsById: Record<string, ModificationGroup>;
}

export interface CartAddItemPayload {
  menuItemId: string;
  name: string;
  basePriceCents: number;
  quantity: number;
  imageUrl?: string;
  caloriesLabel?: string;
  modifiers?: CartModifierGroup[];
  backendItem: BackendCartItem;
}

/**
 * Derives cart line-item payloads (ready for cartSlice's `addItem`) from a
 * reservation's already-placed order items, resolving item/modifier guids
 * against the currently-loaded menu.
 *
 * Extracted from PreorderHome's "Edit Order" cart-seeding logic so the same
 * derivation backs both the manual Edit Order flow and automatic cart
 * hydration on reservation load — the two must never diverge, since a
 * divergence would let a guest submit a cart that doesn't match what
 * "Edit Order" would have produced for the same order.
 *
 * An order item whose stored guid no longer resolves against the current menu
 * (item removed from the menu entirely) is still included, using its raw
 * guid as a fallback name and $0 as a fallback price — the same graceful
 * degradation PreorderHome has always used for this case. Dropping it here
 * instead would silently remove it from the guest's real order the next
 * time the cart is submitted through the edit path, since editOrder
 * replaces the order wholesale rather than merging.
 */
export function buildCartItemsFromOrder(
  orderItems: unknown[],
  menu: OrderToCartMenuLookup,
): CartAddItemPayload[] {
  const items = Array.isArray(orderItems) ? orderItems : [];

  return items.map((raw) => {
    const it = raw as any;
    const itemGuid = String(it?.item?.guid ?? "");
    // A stored guid is the menu item's `_id` (written since FOYR-424) or, for
    // an older order, its POS id; itemsById is indexed by both.
    const menuItem =
      menu.itemsById[itemGuid] || menu.items.find((m) => m.id === itemGuid);
    const name = menuItem?.name || itemGuid || "Item";
    const basePriceCents = menuItem?.priceCents || 0;
    const qty = Number(it?.quantity ?? 1);
    const backendMods = Array.isArray(it?.modifiers) ? it.modifiers : [];
    const modifiersGroups = backendMods.reduce(
      (groups: CartModifierGroup[], m: any) => {
        const group = menu.modificationsById[String(m?.optionGroup?.guid)];
        if (!group) return groups;
        const optId = String(m?.item?.guid ?? "");
        const opt = group.data?.find(
          (o: any) => (o as any).posId === optId || o.id === optId,
        );
        if (!opt) return groups;
        const existingGroup = groups.find(
          (g: any) => g.mod_id === group.mod_id,
        );
        const option = {
          id: opt.id,
          displayName: opt.displayName,
          priceCents: Number(opt?.prices?.[0]?.price ?? 0),
          quantity: Number(m?.quantity ?? 1),
        };
        if (existingGroup) {
          existingGroup.options.push(option);
        } else {
          groups.push({
            mod_id: group.mod_id,
            displayName: group.displayName ?? group.name ?? "",
            options: [option],
          });
        }
        return groups;
      },
      [] as CartModifierGroup[],
    );

    const backendItem: BackendCartItem = {
      itemGroup: (menuItem as any)?.posCategoryIds?.[0]
        ? {
            guid: String((menuItem as any).posCategoryIds[0]),
          }
        : undefined,
      // FOYR-424 R15: the line identity is always the menu item's `_id`, so
      // an older order stored under POS ids is rewritten on its next edit.
      // An item no longer on the menu (or one from a menu persisted before
      // `menuItemId` existed) keeps its stored guid verbatim, so a replacing
      // PUT sends back what is stored rather than "undefined".
      item: {
        guid: menuItem?.menuItemId || itemGuid,
        name,
      },
      quantity: qty,
      modifiers: backendMods.map((m: any) => {
        const group =
          menu.modificationsById[String(m?.optionGroup?.guid)] || undefined;
        const optId = String(m?.item?.guid ?? "");
        const opt = group?.data?.find((o: any) => (o as any).posId === optId);
        return {
          // Stored modifiers carry the POS option guid in item.guid (as
          // SingleItemPage writes them) and no `pos` key. Round-trip the
          // stored guid and names; the menu only refreshes the names when
          // the group happens to be loaded.
          optionGroup: {
            guid: String(m?.optionGroup?.guid),
            name: group?.displayName ?? group?.name ?? m?.optionGroup?.name ?? "",
          },
          item: {
            guid: optId,
            name: opt?.displayName ?? m?.item?.name ?? "",
          },
          quantity: Number(m?.quantity ?? 1),
          modifiers: Array.isArray(m?.modifiers) ? m.modifiers : [],
        };
      }),
    };

    return {
      // The menu's own id, not the stored guid: the stored guid may be the
      // `_id` while menu items (and every line added from the menu) key on
      // `id`. Keying on the stored guid would split one item into two cart
      // lines and hide it from the menu's quantity badges.
      menuItemId: menuItem?.id || itemGuid,
      name,
      basePriceCents,
      quantity: qty,
      imageUrl: menuItem?.imageUrl,
      caloriesLabel: menuItem?.caloriesLabel,
      modifiers: modifiersGroups.length ? modifiersGroups : undefined,
      backendItem,
    };
  });
}

/**
 * Fingerprint of a stored order's items. The cart records the key of the
 * order it was hydrated from; a cart whose key doesn't match the
 * reservation's current order does not hold that order (FOYR-469).
 */
export function orderSourceKey(orderItems: readonly unknown[]): string {
  return JSON.stringify(orderItems);
}

/** Whether a cart holding `cartKey` was hydrated from exactly these stored items. */
export function isCartHydratedFromOrder(
  cartKey: string | undefined,
  orderItems: readonly unknown[],
): boolean {
  return cartKey === orderSourceKey(orderItems);
}

/**
 * The cart action that hydrates from a stored order - the one path shared
 * by page load, "Edit Order", "Add more" and the replacing-PUT guard, all
 * derived through buildCartItemsFromOrder.
 */
export function hydrateCartFromOrder(
  orderItems: unknown[],
  menu: OrderToCartMenuLookup,
  mode: CartHydrationMode,
) {
  return hydrateFromOrder({
    items: buildCartItemsFromOrder(orderItems, menu),
    sourceKey: orderSourceKey(orderItems),
    mode,
  });
}
